import { useQuery, UseQueryResult } from 'react-query';
import { useCallback } from 'react';
import { PricingVersion, PromoteMode, UploadError } from './types/versions';
import { PricingRule } from './types/rules';
import apiClient from 'ps-components/lib/apiClient';
import addAlert from 'ps-components/AlertToast/addAlert';
import moment from 'moment';
import fileDownload from 'js-file-download';

interface DefaultReturn {
  status: number;
}

interface PricingVersionReturn extends DefaultReturn {
  data: PricingVersion;
}

interface PricingRuleReturn extends DefaultReturn {
  data: PricingRule[];
}

interface PricingVersionListReturn extends DefaultReturn {
  data: PricingVersion[];
}

export interface RuleBasedPricingRule {
  id: string;
  name: string;
  lowerLimitEE: number;
  lowerLimitCO: number;
  lastModifiedBy: {
    firstName: string;
    lastName: string;
  };
}

export interface MarketElement {
  source_field: string;
  field_title: string;
}

export interface MarketElements {
  payElements: MarketElement[];
}

export interface JobCodeKeys {
  jobCodeKeys: string[];
}

export enum APPLY_VERSION_CHOICES {
  OVERWRITE_ALL_AND_PRESERVE = 'Overwrite Applicable and Preserve Existing Pricing Matches',
  OVERWRITE_EXISTING = 'Update Applicable Data Cuts',
  CREATE_NEW = 'Create or Append Pricing Matches'
}

type UseQueryHookReturn<T> = UseQueryResult<T> & { key: string };

const getVersionKey = (masterAccountId: string, id = ''): string =>
  `/cms/api/accounts/${masterAccountId}/global-job-pricing/versions${id && '/' + id}`;

export async function getRules(masterAccountId: string, versionId: string): Promise<PricingRuleReturn> {
  const getUrl = getVersionKey(masterAccountId, versionId) + '/rules';
  return (await apiClient.apiGet(getUrl)).data;
}

export async function createRule(masterAccountId: string, details: PricingRule): Promise<PricingRuleReturn> {
  return (
    await apiClient.apiPost(`/cms/api/accounts/${masterAccountId}/global-job-pricing/rules/`, {
      ...details
    })
  ).data;
}

export async function getVersions(masterAccountId: string): Promise<PricingVersionListReturn> {
  const getUrl = getVersionKey(masterAccountId);
  return (await apiClient.apiGet(getUrl)).data;
}

export async function getVersion(masterAccountId: string, id: string): Promise<PricingVersionReturn> {
  const url = getVersionKey(masterAccountId, id);
  return (await apiClient.apiGet(url)).data;
}

export async function createVersion(masterAccountId: string, details: PricingVersion): Promise<PricingVersionReturn> {
  return (
    await apiClient.apiPost(`/cms/api/accounts/${masterAccountId}/global-job-pricing/versions`, {
      ...details
    })
  ).data;
}

export async function copyVersion(masterAccountId: string, id: string): Promise<PricingVersionReturn> {
  const existingVersion = await getVersion(masterAccountId, id);
  const existingRules = await getRules(masterAccountId, id);
  const newVersion = await createVersion(masterAccountId, existingVersion.data);
  for (const rule of existingRules.data) {
    const copiedRule = {
      ...rule,
      globalJobPricingVersionId: newVersion.data.id
    };
    await createRule(masterAccountId, copiedRule);
  }

  return newVersion;
}

export async function updateVersion(
  masterAccountId: string,
  id: string,
  versionDetails: PricingVersion
): Promise<PricingVersionReturn> {
  return (
    await apiClient.apiPut(`/cms/api/accounts/${masterAccountId}/global-job-pricing/versions/${id}`, {
      ...versionDetails
    })
  ).data;
}

export async function deleteVersion(masterAccountId: string, id: string | number): Promise<DefaultReturn> {
  return (await apiClient.apiDelete(`/cms/api/accounts/${masterAccountId}/global-job-pricing/versions/${id}`)).data;
}

export async function runTheRules(masterAccountId: string, id: string | number): Promise<string | number> {
  await apiClient.apiGet(`/cms/api/accounts/${masterAccountId}/global-job-pricing/versions/${id}/run`);
  return id;
}

/**
 * Hooks
 */

export function useVersions(masterAccountId: string): UseQueryHookReturn<PricingVersionListReturn> {
  const getUrl = getVersionKey(masterAccountId);
  const populateVersions = useCallback(async () => await getVersions(masterAccountId), [masterAccountId]);
  return { key: getUrl, ...useQuery(getUrl, populateVersions) };
}

export function useVersion(masterAccountId: string, id: string): UseQueryHookReturn<PricingVersionReturn> {
  const getUrl = getVersionKey(masterAccountId, id);
  const populateVersion = useCallback(async () => await getVersion(masterAccountId, id), [getUrl]);
  return { key: getUrl, ...useQuery(getUrl, populateVersion) };
}

export function useRules(masterAccountId: string, versionId: string): UseQueryHookReturn<PricingRuleReturn> {
  const getUrl = getVersionKey(masterAccountId, versionId) + '/rules';
  const populateRules = useCallback(async () => await getRules(masterAccountId, versionId), [getUrl]);
  return { key: getUrl, ...useQuery(getUrl, populateRules) };
}

export async function updateRule(
  masterAccountId: string,
  id: string | number,
  ruleDetails: PricingRule
): Promise<PricingRuleReturn> {
  return (
    await apiClient.apiPut(`/cms/api/accounts/${masterAccountId}/global-job-pricing/rules/${id}`, {
      ...ruleDetails
    })
  ).data;
}

export async function deleteRule(masterAccountId: string, id: string | number): Promise<DefaultReturn> {
  return (await apiClient.apiDelete(`/cms/api/accounts/${masterAccountId}/global-job-pricing/rules/${id}`)).data;
}

export async function getRulesBasedPricingRules(): Promise<RuleBasedPricingRule[]> {
  return (await apiClient.apiGet(`/api/rules/rules-list/ruleList`, { params: { notEmpty: true } })).data;
}

export async function getMarketPricingElements(): Promise<MarketElements> {
  return (await apiClient.apiGet(`/api/market-pricing/global-job-pricing/getMarketElements`)).data;
}

export function downloadVersion(versionId: string, csrfPair: {csrf_token: string; csrf_timestamp: string}): void {
  const { subsite } = window.app;
  window.location.href = `${window.location.origin}/${subsite}/api/market-pricing/global-job-pricing/downloadRunTheRules?versionId=${versionId}&csrf_token=${csrfPair.csrf_token}&csrf_timestamp=${csrfPair.csrf_timestamp}`;
}

export async function getUniqueJobCodeKeys(versionId: string | number): Promise<JobCodeKeys> {
  return (await apiClient.apiGet(`/api/market-pricing/global-job-pricing/getUniqueJobCodeKeys`,
    {
      params: { versionId }
    }
  )).data;
}

export async function promoteVersion(masterAccountId: string, versionId: string | number, promoteMode: PromoteMode): Promise<DefaultReturn> {
  return (
    await apiClient.apiPost(`/cms/api/accounts/${masterAccountId}/global-job-pricing/versions/${versionId}/promote`,
      { mode: promoteMode })
  ).data;
}

async function startPoll(fileResult: any, csrfPair: {csrf_token: string; csrf_timestamp: string}): Promise<void> {
  return new Promise((resolve, reject) => {
    const POLL_INTERVAL = 2000;
    const MAX_POLL_TIME = 360000;

    const pollUrl = fileResult.pollUrl.substring(fileResult.pollUrl.indexOf('/api/'));
    let pollTime = 0;
    const pollInterval = setInterval(async () => {
      let pollResult;
      try {
        pollResult = await apiClient.apiGet(pollUrl);
      } catch (e) {
        clearInterval(pollInterval);
        console.error(e);
        reject(e);
      }
      if (pollResult.data.ready === true) {
        clearInterval(pollInterval);
        resolve();
        const { subsite } = window.app;
        window.location.href = `${window.location.origin}/${subsite}/api/generic-utils/generic-download/downloadGeneric?pretty_name=${fileResult.resultFileName}&file_name=${fileResult.resultFileName + '.xlsx'}&download_type=xlsx&csrf_token=${csrfPair.csrf_token}&csrf_timestamp=${csrfPair.csrf_timestamp}`;
      } else if (pollTime > MAX_POLL_TIME) {
        clearInterval(pollInterval);
        reject('max timeout reached');
      }
      pollTime += POLL_INTERVAL;
    }, POLL_INTERVAL);
  });
}

export async function generateReport(reportType: string, csrfPair: {csrf_token: string; csrf_timestamp: string}, versionIds: number[], includeOfficialPricing = false): Promise<void> {
  try {
    const result = (await apiClient.apiGet(`/api/market-pricing/global-job-pricing/generateReport`,
      {
        params: { categoryCode: reportType }
      }
    )).data;

    const reportParams: any = {};
    if (versionIds.length) {
      reportParams["pricing_versions"] = versionIds;
    }

    reportParams["include_official_pricing"] = includeOfficialPricing;

    const fileResult = (await apiClient.apiGet(
      `/api/generic-grid/query/downloadResults?report_pk=${result.reportPk}&category_pk=${result.categoryPk}&downloadMode=xlsx&downloadFileName=${reportType}&params=${JSON.stringify(reportParams)}`,
    )).data;

    await startPoll(fileResult, csrfPair);
    addAlert('Report has been generated', 'success', 5000);
  } catch (e) {
    console.log(e);
    addAlert('Could not retrieve report.', 'error');
  }
}

export async function getUploadErrors(): Promise<void> {
  try {
    return await apiClient.apiGet(`/api/market-pricing/global-job-pricing/getUploadErrors`);
  } catch (e) {
    console.log(e);
  }
}

export async function downloadUploadErrorsToExcel(rows: UploadError[], csrfPair: {csrf_token: string; csrf_timestamp: string}): Promise<void> {
  try {
    const response = await apiClient.apiPost(`/api/market-pricing/global-job-pricing/downloadGJMUploadErrors`, {
        rows,
        csrf_token: csrfPair.csrf_token,
        csrf_timestamp: csrfPair.csrf_timestamp
      }, {
        responseType: 'arraybuffer'
      }
    );
    fileDownload(response.data, `upload-errors-${moment().format('MM-DD-YYYY')}.xlsx`);
  } catch (e) {
    console.log(e);
  }
}
